/**
 * @file IntervalsAuth.ts
 * @purpose Handles OAuth2 PKCE flow for Intervals.icu
 */

import { getTransientItem, removeTransientItem, setTransientItem } from '@/lib/storage/transient';
import { createLogger } from '@/lib/logger';

const log = createLogger('IntervalsAuth');

// Session-scoped keys for PKCE verifier/state during OAuth redirects.
const PKCE_STORAGE_KEY = 'intervals_pkce_verifier';
const STATE_STORAGE_KEY = 'intervals_oauth_state';

export interface TokenResponse {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    scope: string;
    athlete: {
        id: string;
        dob?: string;      // Date of birth if included in token response
        icu_ctl?: number;  // Current training load if included
    };
}

class IntervalsAuth {
    // Configuration - pulled from env or constants
    private get clientId() {
        return import.meta.env.VITE_INTERVALS_CLIENT_ID || '';
    }

    private get clientSecret() {
        return import.meta.env.VITE_INTERVALS_CLIENT_SECRET || '';
    }

    private get redirectUri() {
        const configured = (import.meta.env.VITE_INTERVALS_REDIRECT_URI || '').trim();
        if (configured) {
            return this.normalizeRedirectUri(configured);
        }

        if (typeof window !== 'undefined' && window.location?.origin) {
            const origin = window.location.origin.includes('adaptiveendurance.app')
                ? 'https://adaptiveendurance.app'
                : window.location.origin;
            return `${origin}/auth/callback/intervals`;
        }

        return 'http://localhost:5173/auth/callback/intervals';
    }

    private authUrl = 'https://intervals.icu/oauth/authorize';
    private tokenUrl = 'https://intervals.icu/api/oauth/token';

    constructor() {
        // Validation check on load
        if (!import.meta.env.VITE_INTERVALS_CLIENT_ID) {
            log.warn('init', 'VITE_INTERVALS_CLIENT_ID is not set in environment variables');
        }
    }

    private normalizeRedirectUri(uri: string): string {
        return uri
            .replace('http://www.adaptiveendurance.app', 'https://adaptiveendurance.app')
            .replace('https://www.adaptiveendurance.app', 'https://adaptiveendurance.app');
    }

    /**
     * Start the OAuth PKCE flow
     * @param customState Optional custom state to pass through OAuth flow (e.g., invite tokens)
     */
    async startAuth(customState?: Record<string, unknown>) {
        const id = this.clientId;
        if (!id) {
            log.error('startAuth', 'VITE_INTERVALS_CLIENT_ID is missing');
            throw new Error('Client ID not configured. Please check your .env file and rebuild.');
        }

        // 1. Generate PKCE verifier and challenge
        const verifier = this.generateRandomString(43);
        const challenge = await this.pkceChallengeFromVerifier(verifier);

        // 2. Store verifier for the callback
        setTransientItem(PKCE_STORAGE_KEY, verifier);

        // 3. Generate csrf token
        const csrf = this.generateRandomString(16);

        // 4. Construct state object
        const stateObj = { csrf, ...customState };
        const stateString = btoa(JSON.stringify(stateObj));

        // Store CSRF token for verification
        setTransientItem(STATE_STORAGE_KEY, csrf);

        // 5. Construct URL
        const params = new URLSearchParams({
            client_id: this.clientId,
            redirect_uri: this.redirectUri,
            response_type: 'code',
            // Intervals.icu requires access_type: 'offline' (param) for refresh tokens
            // NOT offline_access (scope)
            scope: 'ACTIVITY:WRITE,WELLNESS:WRITE,CALENDAR:WRITE,SETTINGS:WRITE,CHATS:WRITE,LIBRARY:WRITE',
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: stateString,
            access_type: 'offline',
            prompt: 'consent', // Force re-consent to ensure we get a refresh token
        });

        // 6. Redirect
        window.location.href = `${this.authUrl}?${params.toString()}`;
    }

    /**
     * Handle the callback after redirect
     * @returns TokenResponse and the original custom state
     */
    async handleCallback(code: string, state: string): Promise<{ tokens: TokenResponse, customState: Record<string, unknown> }> {
        // 1. Verify state (CSRF)
        let stateObj: Record<string, unknown> = {};
        try {
            stateObj = JSON.parse(atob(state));
        } catch (e) {
            log.error('handleCallback', 'Failed to parse state', { error: e instanceof Error ? e.message : String(e) });
            throw new Error('Invalid state format');
        }

        const storedCsrf = getTransientItem(STATE_STORAGE_KEY);
        if (stateObj.csrf !== storedCsrf) {
            throw new Error('Invalid state parameter (CSRF protection)');
        }

        // 2. Retrieve verifier
        const verifier = getTransientItem(PKCE_STORAGE_KEY);
        if (!verifier) {
            throw new Error('PKCE verifier not found (session expired?)');
        }

        // 3. Exchange code for token
        const response = await fetch(this.tokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                client_id: this.clientId,
                client_secret: this.clientSecret,
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: this.redirectUri,
                code_verifier: verifier,
            }),
        });

        if (!response.ok) {
            const err = await response.text();
            throw new Error(`Failed to exchange token: ${err}`);
        }

        // 4. Cleanup
        removeTransientItem(PKCE_STORAGE_KEY);
        removeTransientItem(STATE_STORAGE_KEY);

        const tokens = await response.json();
        return { tokens, customState: stateObj };
    }

    /**
     * Refresh the access token
     */
    async refreshToken(refreshToken: string): Promise<TokenResponse> {
        const response = await fetch(this.tokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                client_id: this.clientId,
                client_secret: this.clientSecret,
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
            }),
        });

        if (!response.ok) {
            const err = await response.text();
            throw new Error(`Failed to refresh token: ${err}`);
        }

        return await response.json();
    }

    /**
     * Clear auth state (used for logout/disconnect)
     */
    disconnect() {
        removeTransientItem(PKCE_STORAGE_KEY);
        removeTransientItem(STATE_STORAGE_KEY);
    }

    // --- PKCE Helpers ---

    private generateRandomString(length: number): string {
        const array = new Uint8Array(length);
        window.crypto.getRandomValues(array);
        return Array.from(array, (byte) => ('0' + (byte & 0xFF).toString(16)).slice(-2)).join('');
    }

    private async pkceChallengeFromVerifier(v: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(v);
        const hash = await window.crypto.subtle.digest('SHA-256', data);
        return this.base64UrlEncode(hash);
    }

    private base64UrlEncode(arrayBuffer: ArrayBuffer): string {
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }
}

export const intervalsAuth = new IntervalsAuth();
